import React, { useCallback, useEffect, useState } from "react";
import useApi from "@/hooks/useApi";

interface ShopperSummary {
  totalCustomers: number;
  totalLtv: number;
  avgOrderValue: number;
  activeCarts: number;
  lastSyncedAt: string | null;
}

interface EcommerceContact {
  id: number;
  contactId: number | null;
  platform: number;
  platformName: string;
  externalCustomerId: string;
  email: string | null;
  firstName: string | null;
  lastName: string | null;
  totalSpent: number | null;
  orderCount: number;
  lastOrderDate: string | null;
  abandonedCartCount: number;
  tagsJson: string | null;
  syncedAt: string;
}

interface EcommerceOrder {
  id: number;
  externalOrderId: string;
  total: number | null;
  currency: string | null;
  status: string;
  lineItemsJson: string | null;
  placedAt: string | null;
}

interface ContactsResponse {
  total: number;
  page: number;
  pageSize: number;
  contacts: EcommerceContact[];
}

interface Props {
  platform: string;
}

export default function ShopperProfileTab({ platform }: Props) {
  const api = useApi();
  const [summary, setSummary] = useState<ShopperSummary | null>(null);
  const [contacts, setContacts] = useState<EcommerceContact[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [orders, setOrders] = useState<Record<number, EcommerceOrder[]>>({});
  const [ordersLoading, setOrdersLoading] = useState<Record<number, boolean>>({});
  const PAGE_SIZE = 50;

  const fetchData = useCallback(async (p: number) => {
    setLoading(true);
    try {
      const [summaryRes, contactsRes] = await Promise.all([
        api.messaging.get(`EcommerceData/summary?platform=${platform}`),
        api.messaging.get(`EcommerceData/contacts?platform=${platform}&page=${p}&pageSize=${PAGE_SIZE}`)
      ]);
      if (summaryRes.ok) setSummary(await summaryRes.json());
      if (contactsRes.ok) {
        const data: ContactsResponse = await contactsRes.json();
        setContacts(Array.isArray(data.contacts) ? data.contacts : []);
        setTotal(data.total ?? 0);
      }
    } catch {
      setContacts([]);
    } finally {
      setLoading(false);
    }
  }, [api.messaging, platform]);

  useEffect(() => { fetchData(page); }, [fetchData, page]);

  const toggleExpand = useCallback(async (id: number) => {
    if (expandedId === id) { setExpandedId(null); return; }
    setExpandedId(id);
    if (!orders[id]) {
      setOrdersLoading(prev => ({ ...prev, [id]: true }));
      try {
        const res = await api.messaging.get(`EcommerceData/contacts/${id}/orders`);
        if (res.ok) {
          const data = await res.json();
          setOrders(prev => ({ ...prev, [id]: data }));
        }
      } catch { /* non-fatal */ } finally {
        setOrdersLoading(prev => ({ ...prev, [id]: false }));
      }
    }
  }, [api.messaging, expandedId, orders]);

  const fmt = (n: number | null | undefined, prefix = "$") =>
    n != null ? `${prefix}${n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : "—";

  const fmtDate = (d: string | null | undefined) =>
    d ? new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—";

  const totalPages = Math.ceil(total / PAGE_SIZE);

  return (
    <div>
      {/* Summary bar */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
        {[
          { label: "Total Shoppers", value: summary ? summary.totalCustomers.toLocaleString() : "—" },
          { label: "Total LTV", value: fmt(summary?.totalLtv) },
          { label: "Avg Order Value", value: fmt(summary?.avgOrderValue) },
          { label: "Abandoned Carts", value: summary ? summary.activeCarts.toLocaleString() : "—" },
        ].map((stat) => (
          <div key={stat.label} className="rounded-xl border border-zinc-200 bg-white p-4 shadow-sm">
            <div className="text-xs text-slate-500 mb-1">{stat.label}</div>
            <div className="text-xl font-bold text-slate-800">{stat.value}</div>
          </div>
        ))}
      </div>

      {summary?.lastSyncedAt && (
        <div className="text-xs text-slate-400 mb-3">
          Last synced: {new Date(summary.lastSyncedAt).toLocaleString()}
        </div>
      )}

      {/* Shoppers table */}
      <div className="bg-white rounded-xl border border-zinc-200 shadow-sm overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
          <div className="font-semibold text-slate-700 text-sm">
            Shoppers
            {total > 0 && (
              <span className="ml-2 text-xs font-normal text-slate-400">{total.toLocaleString()} total</span>
            )}
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12 text-slate-400 text-sm">Loading shoppers…</div>
        ) : contacts.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-slate-400">
            <div className="text-sm font-medium mb-1">No shoppers synced yet</div>
            <div className="text-xs text-slate-400">Ecommerce sync runs hourly. Check back soon.</div>
          </div>
        ) : (
          <>
            {/* Table header */}
            <div className="grid grid-cols-[2fr_1fr_1fr_1fr_1fr_0.5fr] gap-2 px-4 py-2 bg-slate-50 text-xs font-semibold text-slate-500 uppercase tracking-wide border-b border-slate-100">
              <div>Shopper</div>
              <div>LTV</div>
              <div>Orders</div>
              <div>Last Order</div>
              <div>Carts</div>
              <div></div>
            </div>

            {contacts.map((c) => (
              <React.Fragment key={c.id}>
                <div
                  className="grid grid-cols-[2fr_1fr_1fr_1fr_1fr_0.5fr] gap-2 px-4 py-3 border-b border-slate-50 hover:bg-slate-50 cursor-pointer items-center"
                  onClick={() => toggleExpand(c.id)}
                >
                  <div>
                    <div className="text-sm font-medium text-slate-800">
                      {[c.firstName, c.lastName].filter(Boolean).join(" ") || "Unknown"}
                    </div>
                    <div className="text-xs text-slate-400">{c.email ?? "—"}</div>
                    {c.tagsJson && (() => {
                      try {
                        const tags: string[] = JSON.parse(c.tagsJson);
                        return (
                          <div className="flex flex-wrap gap-1 mt-1">
                            {tags.slice(0, 3).map(t => (
                              <span key={t} className="text-[10px] bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded-full">{t}</span>
                            ))}
                          </div>
                        );
                      } catch { return null; }
                    })()}
                  </div>
                  <div className="text-sm font-semibold text-slate-700">{fmt(c.totalSpent)}</div>
                  <div className="text-sm text-slate-700">{c.orderCount}</div>
                  <div className="text-sm text-slate-500">{fmtDate(c.lastOrderDate)}</div>
                  <div className="text-sm">
                    {c.abandonedCartCount > 0 ? (
                      <span className="text-amber-600 font-medium">{c.abandonedCartCount}</span>
                    ) : (
                      <span className="text-slate-400">0</span>
                    )}
                  </div>
                  <div className="flex justify-end">
                    <svg
                      className={`w-4 h-4 text-slate-400 transition-transform ${expandedId === c.id ? "rotate-180" : ""}`}
                      viewBox="0 0 16 16" fill="currentColor"
                    >
                      <path d="M8 10.5L3 5.5H13L8 10.5Z" />
                    </svg>
                  </div>
                </div>

                {/* Expanded order history */}
                {expandedId === c.id && (
                  <div className="bg-slate-50 border-b border-slate-100 px-6 py-4">
                    <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3">Order History</div>
                    {ordersLoading[c.id] ? (
                      <div className="text-xs text-slate-400">Loading orders…</div>
                    ) : (orders[c.id] ?? []).length === 0 ? (
                      <div className="text-xs text-slate-400">No orders found.</div>
                    ) : (
                      <div className="space-y-2">
                        {(orders[c.id] ?? []).map(o => (
                          <div key={o.id} className="flex items-center gap-4 bg-white rounded-lg border border-slate-200 px-3 py-2 text-xs">
                            <div className="font-mono text-slate-600 flex-shrink-0">#{o.externalOrderId}</div>
                            <div className="font-semibold text-slate-700">{fmt(o.total)}{o.currency ? ` ${o.currency}` : ""}</div>
                            <div className={`px-1.5 py-0.5 rounded-full font-medium ${
                              o.status === "fulfilled" ? "bg-green-100 text-green-700"
                              : o.status === "cancelled" ? "bg-red-100 text-red-600"
                              : "bg-blue-100 text-blue-700"
                            }`}>{o.status}</div>
                            <div className="text-slate-400 ml-auto">{fmtDate(o.placedAt)}</div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </React.Fragment>
            ))}
          </>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-4">
          <div className="text-xs text-slate-500">
            Page {page} of {totalPages} ({total.toLocaleString()} shoppers)
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              disabled={page <= 1}
              onClick={() => setPage(p => p - 1)}
              className="text-xs px-3 py-1.5 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 disabled:opacity-40"
            >
              Previous
            </button>
            <button
              type="button"
              disabled={page >= totalPages}
              onClick={() => setPage(p => p + 1)}
              className="text-xs px-3 py-1.5 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 disabled:opacity-40"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
